import type DateRange from "shared/helpers/DateRange";
import type StreamFilters from "shared/helpers/StreamFilters";
import type { StreamRequestFilters } from "shared/helpers/StreamFilters";
import {
  type AIVisibilityProjectEntity,
  type AIVisibilityProjectSource,
} from "shared/modules/AIVisibility/types";
import {
  type ChartsWithInstagramHandleSelection,
  type ChartsWithOutreachActivitySelection,
  type ChartsWithOutreachInsightsMetrics,
  type ChartsWithOutreachPerformanceMetrics,
  type ChartsWithPostSelection,
  type ChartsWithSocialAccountSelection,
  type ChartsWithSocialEngageInsightsMetrics,
  type ChartsWithSocialEngagePerformanceMetrics,
  type ChartsWithSocialInsightsMetrics,
  type ChartsWithSovMetricType,
  type ChartsWithStreamInsightsMetrics,
} from "shared/modules/Charts/types";
import type { Chart } from "shared/modules/Charts/types/chartTypes";
import type { KeyQueryParams } from "shared/modules/Charts/types/data";
import type { MediaReleaseType } from "shared/resources/MediaRelease";
import type { OutreachActivityStatusType } from "shared/resources/OutreachActivity";
import type { OutreachCommunicationType } from "shared/resources/OutreachCommunication";
import type { Nullable, Uuid } from "shared/types";
import type { MentionType } from "shared/types/mentions";

export interface BaseChartDetails {
  info_tooltip?: string;
  label?: string;
}

export enum ChartVariant {
  Area = "line_area",
  Doughnut = "doughnut",
  HorizontalBar = "bar_horizontal",
  KeyInsight = "key_insight",
  Line = "line",
  Map = "map",
  Pie = "pie",
  Radar = "radar",
  StackedHorizontalBar = "bar_horizontal_stacked",
  StackedVerticalBar = "bar_stacked",
  Table = "table",
  VerticalBar = "bar",
  WordCloud = "word_cloud",
}

export enum ChartAxisInterval {
  Day = "day",
  Month = "month",
  Quarter = "quarter",
  Week = "week",
  Year = "year",
}

export const CHART_AXIS_INTERVALS = Object.values(ChartAxisInterval);
export interface BaseChartSetting extends BaseChartDetails {
  advanced_filters?: Nullable<StreamFilters | StreamRequestFilters>;
  chart?: Chart;
  chart_variant?: ChartVariant;
  full_width?: boolean;
  interval?: ChartAxisInterval;
  notes?: string;
  range?: DateRange;
  uuid?: Uuid;
}

export interface ChartSettingWithStreamsAndMediaTypes extends BaseChartSetting {
  stream_ids: number[];
  types: MentionType[];
}

export interface ChartSettingWithStockSymbol extends ChartSettingWithStreamsAndMediaTypes {
  requiresStockSymbol: boolean;
  stock_exchange?: string;
  stock_symbol: string;
}

export interface ChartSettingWithStreamComparison extends ChartSettingWithStreamsAndMediaTypes {
  stream_ids: number[];
}

export interface ChartExportOption {
  id: string;
  label: string;
  value: boolean;
}

export interface ChartSettingWithExportOptions extends BaseChartSetting {
  export_options: ChartExportOption[];
}

export enum TopArticlesMetric {
  AdvertisementRate = "advertisement_rate",
  Audience = "audience",
  DomainAuthority = "domain_authority",
  ImpactScore = "impact_score",
  SentimentNegative = "sentiment_negative",
  SentimentPositive = "sentiment_positive",
  SocialShares = "social_shares",
}

export const DEFAULT_TOP_ARTICLES_METRIC = TopArticlesMetric.Audience;

export interface ChartSettingWithSortBy extends ChartSettingWithStreamsAndMediaTypes {
  sort_by: TopArticlesMetric;
}

export interface ChartSettingWithMediaReleases extends BaseChartSetting {
  media_release_ids: number[];
}

export interface ChartSettingWithStreamAndMediaReleases
  extends ChartSettingWithMediaReleases, ChartSettingWithStreamsAndMediaTypes {}

export interface OutreachActivityChartFilters {
  assigneeIds: number[];
  mediaReleaseTypes: MediaReleaseType[];
  statuses: OutreachActivityStatusType[];
  tagIds: number[];
  types: OutreachCommunicationType[];
}

export const DEFAULT_OUTREACH_ACTIVITY_CHART_FILTERS: OutreachActivityChartFilters =
  {
    assigneeIds: [],
    mediaReleaseTypes: [],
    statuses: [],
    tagIds: [],
    types: [],
  };

export function getOutreachActivityChartFilters(
  filters?: Partial<OutreachActivityChartFilters>
): OutreachActivityChartFilters {
  return {
    ...DEFAULT_OUTREACH_ACTIVITY_CHART_FILTERS,
    ...filters,
  };
}

export interface ChartSettingWithOutreachActivityFilters extends BaseChartSetting {
  chart: ChartsWithOutreachActivitySelection;
  outreach_activity_filters: OutreachActivityChartFilters;
}

export interface ChartSettingWithSingleMediaRelease extends BaseChartSetting {
  media_release_id: number;
}

export interface ChartSettingWithKeyMessages extends ChartSettingWithStreamsAndMediaTypes {
  queries: KeyQueryParams[];
}

export type LinkedStreams = Record<string, number>;

export interface ChartSettingWithLinkedStreams extends ChartSettingWithStreamsAndMediaTypes {
  linked_stream_ids: LinkedStreams;
}

export interface ChartSettingWithOptionalLinkedStreams extends ChartSettingWithStreamsAndMediaTypes {
  linked_stream_ids?: LinkedStreams;
}

export interface ChartSettingWithAIVisibility extends BaseChartSetting {
  exclude_llm?: boolean;
  llm_entities: AIVisibilityProjectEntity[];
  llm_models: AIVisibilityProjectSource[];
}

export interface ChartSettingWithContactKeys extends ChartSettingWithStreamsAndMediaTypes {
  show_contacts_only?: boolean;
}

export enum StreamInsightsMetric {
  Audience = "audience",
  AVE = "ave",
  ImpactScore = "impact_score",
  Mentions = "mentions",
  Sentiment = "sentiment",
  SocialShares = "social_shares",
}

export enum SovMetricType {
  AudienceReach = "audience_reach",
  Ave = "ave",
  Mentions = "mentions",
  SocialShares = "social_shares",
}

export const SOV_METRIC_TYPE_OPTIONS = Object.values(SovMetricType);

export const DEFAULT_SOV_METRIC_TYPE = SovMetricType.Mentions;

export enum OutreachInsightsMetric {
  ClickedThrough = "clicked_through",
  ClickedThroughPercentage = "clicked_through_percentage",
  ContactsTargeted = "contacts_targeted",
  Delivered = "delivered",
  DeliveredPercentage = "delivered_percentage",
  Opened = "opened",
  OpenedPercentage = "opened_percentage",
  Sent = "sent",
}

export enum OutreachPerformanceMetric {
  Clicks = "clicks",
  MediaOutreach = "media_outreach",
  Opens = "opens",
}

export enum SocialInsightsMetric {
  Engagement = "engagement",
  Posts = "posts",
  Sentiment = "sentiment",
  TopEmoji = "top_emoji",
}

export enum SocialEngagePerformanceMetric {
  Clicks = "clicks",
  EngagementRate = "engagement_rate",
  Impressions = "impressions",
  Performance = "performance",
  Reach = "reach",
  SocialPosts = "social_posts",
}

export type InsightsMetric =
  | StreamInsightsMetric
  | OutreachInsightsMetric
  | OutreachPerformanceMetric
  | SocialInsightsMetric
  | SocialEngagePerformanceMetric;

export interface ChartSettingWithStreamInsightsMetrics extends ChartSettingWithStreamsAndMediaTypes {
  chart: ChartsWithStreamInsightsMetrics;
  insights_metrics: StreamInsightsMetric[];
}
export interface ChartSettingWithOutreachInsightsMetrics extends ChartSettingWithMediaReleases {
  chart: ChartsWithOutreachInsightsMetrics;
  insights_metrics: OutreachInsightsMetric[];
}

export interface ChartSettingWithOutreachPerformanceMetrics extends ChartSettingWithMediaReleases {
  chart: ChartsWithOutreachPerformanceMetrics;
  insights_metrics: OutreachPerformanceMetric[];
}

export interface ChartSettingWithSocialInsightsMetrics extends ChartSettingWithStreamsAndMediaTypes {
  chart: ChartsWithSocialInsightsMetrics;
  insights_metrics: SocialInsightsMetric[];
}

export interface ChartSettingWithSocialEngagePerformanceMetrics extends BaseChartSetting {
  chart: ChartsWithSocialEngagePerformanceMetrics;
  insights_metrics?: SocialEngagePerformanceMetric[];
  organisation_social_integration_ids: string[];
  post_ids?: number[];
}

export interface ChartSettingWithSocialEngageInsightsMetrics extends BaseChartSetting {
  chart: ChartsWithSocialEngageInsightsMetrics;
  insights_metrics: SocialEngagePerformanceMetric[];
  organisation_social_integration_ids: string[];
  post_ids?: number[];
}

export type ChartSettingWithInsightsMetrics =
  | ChartSettingWithStreamInsightsMetrics
  | ChartSettingWithOutreachInsightsMetrics
  | ChartSettingWithOutreachPerformanceMetrics
  | ChartSettingWithSocialInsightsMetrics
  | ChartSettingWithSocialEngageInsightsMetrics;

export type ChartSettingWithSocialEngageOnly = BaseChartSetting & {
  post_ids: number[];
};

export interface ChartSettingWithPostSelection extends BaseChartSetting {
  chart: ChartsWithPostSelection;
  organisation_social_integration_ids: string[];
  post_ids: number[];
}

export interface ChartSettingWithSocialAccountSelection extends BaseChartSetting {
  chart: ChartsWithSocialAccountSelection;
  organisation_social_integration_ids: string[];
}

export interface ChartSettingWithInstagramHandleSelection extends BaseChartSetting {
  chart: ChartsWithInstagramHandleSelection;
  instagram_handles_organisation_social_integration_ids: string[];
}

export interface ChartSettingWithAnalyticsIntegrationTarget {
  target_id: number;
  target_name: string;
  target_type: string;
}

export interface ChartSettingWithAnalyticsIntegration extends ChartSettingWithStreamsAndMediaTypes {
  analytics_integration_target?: ChartSettingWithAnalyticsIntegrationTarget;
  requires_analytics_integration: boolean;
}

export interface ChartSettingWithSovMetricType extends ChartSettingWithStreamsAndMediaTypes {
  chart: ChartsWithSovMetricType;
  metric_type?: SovMetricType;
}

export type ChartSetting =
  | BaseChartSetting
  | ChartSettingWithStreamsAndMediaTypes
  | ChartSettingWithExportOptions
  | ChartSettingWithMediaReleases
  | ChartSettingWithOutreachActivityFilters
  | ChartSettingWithKeyMessages
  | ChartSettingWithLinkedStreams
  | ChartSettingWithOptionalLinkedStreams
  | ChartSettingWithStockSymbol
  | ChartSettingWithSortBy
  | ChartSettingWithStreamInsightsMetrics
  | ChartSettingWithOutreachInsightsMetrics
  | ChartSettingWithOutreachPerformanceMetrics
  | ChartSettingWithSocialInsightsMetrics
  | ChartSettingWithSocialEngageOnly
  | ChartSettingWithSocialEngagePerformanceMetrics
  | ChartSettingWithSocialEngageInsightsMetrics
  | ChartSettingWithAnalyticsIntegration
  | ChartSettingWithPostSelection
  | ChartSettingWithSocialAccountSelection
  | ChartSettingWithInstagramHandleSelection
  | ChartSettingWithSingleMediaRelease
  | ChartSettingWithSovMetricType
  | ChartSettingWithAIVisibility
  | ChartSettingWithContactKeys;

export type MetricLabelKey = SovMetricType | TopArticlesMetric;

export interface BackendChartConfig extends BaseChartDetails {
  analytics_integration_target?: ChartSettingWithAnalyticsIntegrationTarget;
  arguments?: { visualisation_type?: string };
  chart_variants?: ChartVariant[];
  default_chart_variant?: ChartVariant;
  display_pagination?: boolean;
  export_options?: ChartExportOption[];
  has_double_width?: boolean;
  hide_streams?: boolean;
  media_release_ids?: number[];
  metric_labels?: { [K in MetricLabelKey]?: string };
  metric_tooltips?: { [K in MetricLabelKey]?: string };
  post_ids?: number[];
  range?: DateRange;
  requires_analytics_integration?: boolean;
  selected_by_default?: boolean;
  stream_ids?: number[];
  types?: MentionType[];
}

export type ChartIndex = Record<Partial<Chart>, BackendChartConfig>;

export type BaseSavedChartSetting<T extends ChartSetting = ChartSetting> = Omit<
  BackendChartConfig,
  keyof BaseChartDetails
> &
  Omit<
    Required<
      Omit<
        T,
        | "chart_variant"
        | "full_width"
        | "interval"
        | "notes"
        | "advanced_filters"
      >
    >,
    keyof BaseChartDetails
  > &
  Pick<
    T,
    "chart_variant" | "full_width" | "interval" | "notes" | "advanced_filters"
  >;

export type SavedChartSetting<T extends ChartSetting = ChartSetting> =
  BaseSavedChartSetting<T> & Partial<BaseChartDetails>;

export type SavedChartSettingWithChartDetails<
  T extends ChartSetting = ChartSetting,
> = BaseSavedChartSetting<T> & Required<BaseChartDetails>;

export interface TeaserChartSetting extends Required<
  Omit<
    BaseChartSetting,
    | "chart"
    | "chart_variant"
    | "full_width"
    | "interval"
    | "notes"
    | "advanced_filters"
  >
> {
  chart: "insights_plus_teaser";
  full_width?: boolean;
  interval?: ChartAxisInterval;
}

export type SavedChartSettingOrTeaserChartSetting<
  RequireChartDetails extends boolean = true,
> =
  | (RequireChartDetails extends true
      ? SavedChartSettingWithChartDetails
      : SavedChartSetting)
  | TeaserChartSetting;

export type ChartOrTeaser = SavedChartSettingOrTeaserChartSetting;
